import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, CheckCircle2, BarChart3, Shield, Users, 
  Clock, FileText, AlertTriangle, Layers, Check, X 
} from 'lucide-react';

const Videra = () => {
  const problems = [
    'Project data scattered across dozens of spreadsheets and several email threads',
    'Monthly reporting that takes days to compile',
    'Stakeholders asking questions you can\'t answer quickly',
    'Governance processes that exist on paper but not in practice',
    'No single source of truth for your portfolio'
  ];

  const capabilities = [
    { icon: Layers, title: 'Gated Delivery Tracking', desc: 'Stage-by-stage project progression with mandatory checklists' },
    { icon: Clock, title: 'Lifecycle Management', desc: 'Long-term asset planning aligned to PFI contract obligations' },
    { icon: BarChart3, title: 'Commercial Dashboard', desc: 'Real-time budget, forecast, and variation tracking' },
    { icon: Shield, title: 'Governance Automation', desc: 'Structured approvals, audit trails, and compliance tracking' },
    { icon: Users, title: 'Stakeholder Portals', desc: 'Role-based views for clients, contractors, and executives' },
    { icon: AlertTriangle, title: 'H&S Compliance', desc: 'CDM tracking, risk assessments, and safety documentation' },
    { icon: FileText, title: 'Executive Reporting', desc: 'One-click board packs and portfolio summaries' },
  ];

  const pricingPlans = [
    {
      name: 'Foundation',
      price: '£199',
      period: '/mo',
      description: 'For small teams getting started',
      features: [
        { name: '5 Projects', included: true },
        { name: '2 Users', included: true },
        { name: 'AI Insights', included: false },
        { name: 'Custom Branding', included: false },
        { name: 'API Access', included: false },
        { name: 'Email Support', included: true },
      ],
      highlighted: false
    },
    {
      name: 'Professional',
      price: '£399',
      period: '/mo',
      description: 'For growing organisations',
      features: [
        { name: '50 Projects', included: true },
        { name: '10 Users', included: true },
        { name: 'AI Insights', included: true },
        { name: 'Custom Branding', included: true },
        { name: 'Limited API Access', included: true },
        { name: 'Priority Support', included: true },
      ],
      highlighted: true
    },
    {
      name: 'Enterprise',
      price: '£799',
      period: '/mo',
      description: 'For large-scale operations',
      features: [
        { name: 'Unlimited Projects', included: true },
        { name: 'Unlimited Users', included: true },
        { name: 'AI Insights', included: true },
        { name: 'Custom Branding', included: true },
        { name: 'Full API Access', included: true },
        { name: 'Dedicated Support', included: true },
      ],
      highlighted: false
    },
  ];

  const differentiators = [
    { title: 'Not a generic PM tool', desc: 'Purpose-built for healthcare, FM, and infrastructure sectors' },
    { title: 'Built by practitioners', desc: 'Designed from 20+ years managing £100M+ PFI portfolios' },
    { title: 'Governance-focused', desc: 'Because that\'s where most programmes fail' },
    { title: 'Opinionated by design', desc: 'Best practices baked in so you don\'t have to figure them out' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="videra-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-slate-900 relative overflow-hidden" data-testid="videra-hero">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-blue-400 font-semibold mb-6">
              The Platform
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium tracking-tight text-white leading-[1.1]">
              Videra
            </h1>
            <p className="mt-4 text-2xl md:text-3xl text-slate-300 font-light">
              Portfolio Intelligence. Delivered.
            </p>
          </motion.div>

          {/* Dashboard Mockup */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-16"
          >
            <div className="bg-slate-800 rounded-xl p-2 shadow-2xl max-w-5xl mx-auto">
              <img
                src="https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxkYXNoYm9hcmQlMjB1aSUyMG1vY2t1cHxlbnwwfHx8fDE3NzUzNDA5NDl8MA&ixlib=rb-4.1.0&q=85"
                alt="Videra Dashboard"
                className="w-full rounded-lg"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 md:py-32" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
                The Problem We Solve
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
                Sound familiar?
              </h2>
              <ul className="mt-10 space-y-5">
                {problems.map((problem, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-6 h-6 bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                    <span className="text-lg text-slate-600">{problem}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-900 p-12 text-center"
            >
              <p className="text-slate-400 text-lg mb-4">There's a better way.</p>
              <h3 className="text-3xl md:text-4xl font-medium text-white">
                One platform.<br />
                Complete visibility.<br />
                Real control.
              </h3>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Videra */}
      <section className="py-20 md:py-32 bg-slate-50 border-y border-slate-200" data-testid="what-is-videra">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              What is Videra?
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Purpose-built portfolio management
            </h2>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed">
              Videra is a purpose-built portfolio management platform designed for 
              organisations managing complex project estates — particularly those in 
              PFI, healthcare, and facilities management sectors.
            </p>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed font-medium italic">
              Unlike generic project tools, Videra was built from real-world experience 
              managing £100M+ portfolios. Every feature exists because it solved a real 
              problem on a real programme.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-20 md:py-32" id="features" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              Features
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Key Capabilities
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-8 border border-slate-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300"
                data-testid={`capability-${index}`}
              >
                <div className="w-12 h-12 bg-slate-50 flex items-center justify-center mb-6">
                  <cap.icon className="w-6 h-6 text-slate-700" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-3">{cap.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{cap.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 md:py-32 bg-slate-50 border-y border-slate-200" id="pricing" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              Pricing
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Flexible Plans
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white p-8 ${
                  plan.highlighted 
                    ? 'border-2 border-slate-900 shadow-lg' 
                    : 'border border-slate-200'
                }`}
                data-testid={`pricing-plan-${index}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-4 py-1 text-xs font-semibold tracking-wider">
                    MOST POPULAR
                  </div>
                )}
                <div className="text-center">
                  <h3 className="text-xl font-medium text-slate-900">{plan.name}</h3>
                  <p className="mt-2 text-sm text-slate-500">{plan.description}</p>
                  <div className="mt-6">
                    <span className="text-4xl font-medium text-slate-900">{plan.price}</span>
                    <span className="text-slate-500">{plan.period}</span>
                  </div>
                </div>
                <ul className="mt-8 space-y-4">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-3">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <X className="w-5 h-5 text-slate-300 flex-shrink-0" />
                      )}
                      <span className={feature.included ? 'text-slate-700' : 'text-slate-400'}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>
                {plan.highlighted ? (
                  <a
                    href="https://demo.videra.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 w-full inline-flex items-center justify-center px-6 py-4 font-medium tracking-wide transition-all bg-slate-900 text-white hover:bg-slate-800"
                    data-testid={`pricing-cta-${index}`}
                  >
                    Try Demo
                  </a>
                ) : (
                  <Link
                    to="/contact"
                    className="mt-8 w-full inline-flex items-center justify-center px-6 py-4 font-medium tracking-wide transition-all bg-slate-100 text-slate-900 hover:bg-slate-200"
                    data-testid={`pricing-cta-${index}`}
                  >
                    Get Started
                  </Link>
                )}
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href="https://demo.videra.app"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="request-demo-link"
            >
              Try the Demo
            </a>
            <span className="mx-4 text-slate-300">|</span>
            <Link
              to="/contact"
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="free-trial-link"
            >
              Request a Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Built Different Section */}
      <section className="py-20 md:py-32" data-testid="differentiators-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              Built Different
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Why Videra isn't "just another project tool"
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {differentiators.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4"
                data-testid={`differentiator-${index}`}
              >
                <div className="w-8 h-8 bg-slate-900 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-slate-900">{item.title}</h3>
                  <p className="mt-1 text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900" data-testid="videra-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
              Ready to see Videra in action?
            </h2>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
              Book a demo to see how Videra can transform your portfolio management 
              and give you the visibility you need.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://demo.videra.app"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="videra-demo-cta"
                className="inline-flex items-center justify-center bg-white text-slate-900 px-10 py-5 font-medium tracking-wide hover:bg-slate-100 transition-all group"
              >
                Try the Demo
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-transparent border border-white/30 text-white px-10 py-5 font-medium tracking-wide hover:bg-white/10 transition-all"
              >
                Talk to Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Videra;
